/*
 * Copyright © 2017-Present, The Freshwater Trust, all rights reserved.
 */

import {usePermission} from "../../../Hooks/PermissionHooks";
import {useEffectPromise} from "../../../Hooks/EffectHooks";
import {service as ContextNodeService} from "../../../Services/ContextNodeService";
import {StreamBankLink} from "../StreamBankLink";
import {SbMap} from "../SbMap/SbMap";
import {useState} from "react";
import {ContextNodeLink} from "../ContextNodeLink";

/**
 * Map of a context node's children.
 *
 * Optional props:
 *   mutedFilter — (item) => bool; matching items render muted (grayed out)
 *   showMuted   — when false, muted items are left off the map entirely
 */
export const ContextNodeMap = (props) => {

	const {
		contextNodeId,
		contextNodeTypeId,
		items: itemsFromProps = null,
		mutedFilter = null,
		showMuted = true,
	} = props;
	
	const [items, setItems] = useState(null);
	
	const permission = usePermission();
	
	const loadContextNodes = async () => {
		if(itemsFromProps?.length) {
			return itemsFromProps;
		}
		
		return (await ContextNodeService.GetChildrenContextNodes({
			contextNodeId,
			contextNodeTypeId,
		}))?.result ?? [];
	}
	
	useEffectPromise(
		loadContextNodes,
		setItems,
		[contextNodeId, contextNodeTypeId, itemsFromProps],
		(contextNodeId || itemsFromProps?.length)
	);
	
	const markers = items?.map((contextNode) => {
		const { contextNodeId, name, description, shortTitle, title, Location } = contextNode;

		const muted = mutedFilter?.(contextNode) ?? false;
		if (muted && !showMuted) {
			return undefined;
		}

		if (Location?.latitude && Location.longitude) {
			return ({
				key: contextNodeId,
				lat: Location.latitude,
				lng: Location.longitude,
				accuracy: Location.accuracy,
				muted,
				marker: !permission('authenticated_user') ? 'circle' : 'both',
				title: shortTitle ?? title,
				content: <div>
					<ContextNodeLink contextNodeId={contextNodeId}>
						{contextNode.name}
					</ContextNodeLink>
					<br/>
					{description}
				</div>
			});
		}
	}) ?? [];
	
	return <SbMap
		// lat long info is not on the returned API ContextNodeController
		Lat={markers?.[0]?.lat}
		Lng={markers?.[0]?.lng}
		Zoom={4}
		Markers={markers ?? []}
	/>;
}
